/**
 * Grupos — o que o número monitorado enxerga, e quanto ainda cabe em cada um.
 *
 * Três decisões que valem o comentário:
 *
 *  - **A coluna que importa é a lotação, não a contagem.** O número cru ("312 pessoas") não
 *    diz nada sozinho; o que decide a operação é quanto falta para o grupo sair do rodízio.
 *    Por isso a medida é sempre contra o **limite de segurança**, nunca contra a capacidade
 *    do WhatsApp — medir contra 1024 mostraria folga onde já não há.
 *  - **Contagem ausente é travessão, não zero.** `participantsCount: null` quer dizer "nunca
 *    sincronizei", e um `0` ali diria que o grupo está vazio (invariante 8).
 *  - **Escolher e atualizar são botões DIFERENTES.** "Escolher grupos" abre a lista do
 *    WhatsApp para a pessoa marcar o que quer acompanhar; "Atualizar contagens" só vai ver
 *    como estão os que já entraram. Eram o mesmo gesto enquanto o plano cortava em 5 — com
 *    200 grupos no celular do dono, o mesmo botão arrastaria a família toda para dentro.
 *
 * No celular a tabela vira lista de cartões: `grupos` está na barra inferior, então é tela
 * que se olha em pé, com uma mão (DESIGN_SYSTEM §11).
 */
import { useCallback, useMemo, useState } from 'react';
import { runInSequence } from '../../../lib/sequential';
import { Link, useNavigate } from 'react-router';
import {
  ActionMenu,
  Avatar,
  Badge,
  Button,
  CapacityMeter,
  Card,
  Checkbox,
  Dialog,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  NativeSelect,
  NO_DATA_DASH,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableEntity,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  TableSkeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  cn,
  formatNumber,
  useToast,
} from '@mgdlead/ui';
import type { ActionMenuItem, MonitorStatus } from '@mgdlead/ui';
import {
  ArrowDownUp,
  Clock,
  Download,
  EyeOff,
  Link2Off as LinkIcon,
  ListChecks,
  Pause,
  Play,
  RefreshCw,
  SearchX,
  Trash2,
  UserSearch,
  Users,
  ShieldAlert,
  ShieldCheck,
  TrendingDown,
  Tags,
} from 'lucide-react';
import { can } from '@mgdlead/shared';

import { FormAlert } from '@mgdlead/ui';
import { PageHeader } from '@mgdlead/ui';
import { BaixarContatosDialog } from '../../../features/contatos/BaixarContatosDialog';
import { CategoriesDialog } from './CategoriesDialog';
import { CategoryBadge } from './CategoryBadge';
import { ImportGroupsDialog, isImportable } from './ImportGroupsDialog';
import { OccupancyBar } from './OccupancyBar';
import { SafetyThresholdDialog } from './SafetyThresholdDialog';
import {
  adminsMembersLabel,
  GROUP_ACTION,
  GROUP_STATUS,
  INVITE_MISSING,
  groupDisplayName,
  hasInvite,
  groupRemovalBlocker,
  importSummary,
  occupancy,
  occupancyLabel,
  participantsKnown,
  seatsLeft,
  PARTICIPANTS_NEVER_SYNCED,
  syncSummary,
} from '../../../features/group/group-ui';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { useGruposComEvasaoAnormal } from '../../../features/retention/useGruposComEvasaoAnormal';
import {
  useArchiveGroup,
  useGroupCategories,
  useGroups,
  useImportGroups,
  useMonitors,
  useRemoveGroup,
  useSyncGroupParticipants,
  useUnarchiveGroup,
  useUpdateGroup,
} from '../../../lib/api';
import type { Group, GroupLimit } from '../../../lib/api';
import { formatExact, formatRelative } from '../../../lib/datetime';
import { humanizeError } from '../../../lib/errors';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';

/** A frase que ensina o termo. Uma só, usada em dois lugares. */
const O_QUE_E =
  'Grupos são as conversas de WhatsApp que um número monitorado acompanha. É neles que a entrada e a saída de cada pessoa viram número.';

/**
 * Os dois gestos que pedem confirmação. `stop` tira da operação e volta; `remove` é
 * terminal e só existe para o grupo importado por engano.
 */
type Confirm = { kind: 'stop' | 'remove'; group: Group };

/**
 * Filtro de situação da barra superior. Pausado é decisão de pessoa e vence "cheio", que é
 * consequência da contagem: o grupo pausado não recebe ninguém, cheio ou não.
 */
type Filtro = 'todos' | 'recebendo' | 'cheio' | 'pausado' | 'fora';

/** D5: valor do filtro para "grupos sem categoria" (não colide com uuid). */
const SEM_CATEGORIA = 'sem-categoria';

function filtroDe(group: Group): Exclude<Filtro, 'todos'> {
  if (group.archivedAt !== null) return 'fora';
  if (!group.isActive) return 'pausado';
  if (group.status === 'FULL') return 'cheio';
  return 'recebendo';
}

export function GroupsPage() {
  const { workspace, role } = useWorkspace();
  const { toast } = useToast();
  useDocumentTitle('Grupos');

  const [includeArchived, setIncludeArchived] = useState(false);
  const groupsQuery = useGroups(workspace.id, includeArchived);
  const monitorsQuery = useMonitors(workspace.id);

  const [importOpen, setImportOpen] = useState(false);
  const [threshold, setThreshold] = useState<Group | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [busca, setBusca] = useState('');
  /** D5: recorte por categoria. `''` = todas; `SEM_CATEGORIA` = as sem etiqueta. */
  const [categoria, setCategoria] = useState<string>('');
  const [categoriasOpen, setCategoriasOpen] = useState(false);
  /** Baixar contatos: `null` fechado; a lista é a seleção com que o diálogo abre. */
  const [baixarContatos, setBaixarContatos] = useState<string[] | null>(null);
  const categoriasQuery = useGroupCategories(workspace.id);
  const [selecionados, setSelecionados] = useState<ReadonlySet<string>>(new Set());
  /** Varredura em lote em andamento: `null` quando não há nenhuma. */
  const [varredura, setVarredura] = useState<{ feitos: number; total: number } | null>(null);

  const importGroups = useImportGroups();
  const update = useUpdateGroup();
  const archive = useArchiveGroup();
  const unarchive = useUnarchiveGroup();
  const remove = useRemoveGroup();
  const syncParticipants = useSyncGroupParticipants();

  const canImport = can(role, 'group:import');
  const canUpdate = can(role, 'group:update');
  const canArchive = can(role, 'group:archive');
  const canExportContacts = can(role, 'group:export_contacts');

  const data = groupsQuery.data;
  const groups = useMemo(() => data?.groups ?? [], [data]);
  const limit: GroupLimit | undefined = data?.limit;
  /* Servidor já respondeu "cabe mais um?". Aqui só se LÊ a resposta (invariante 4). */
  const limitReached = limit !== undefined && !limit.unlimited && !limit.allowed;

  const monitors = useMemo(() => monitorsQuery.data?.monitors ?? [], [monitorsQuery.data]);
  const prontos = useMemo(() => monitors.filter(isImportable), [monitors]);

  /**
   * Os grupos em que sabemos QUANTOS, não QUEM.
   *
   * Merece aviso no topo porque a falta é invisível: a barra de lotação enche, a linha do
   * tempo existe, a tela inteira parece em dia — e a ausência só aparece no dia em que
   * alguém tenta exportar os contatos e recebe um arquivo quase vazio.
   */
  const semVarredura = useMemo(
    () => groups.filter((group) => group.archivedAt === null && !participantsKnown(group)),
    [groups],
  );

  const gruposComEvasaoAnormal = useGruposComEvasaoAnormal(workspace.id);

  /** Estado de cada monitorador, para a coluna "Monitorador" (o grupo só traz o nome). */
  const statusDoMonitor = useMemo(() => {
    const mapa = new Map<string, MonitorStatus>();
    for (const monitor of monitorsQuery.data?.monitors ?? []) mapa.set(monitor.id, monitor.status);
    return mapa;
  }, [monitorsQuery.data]);

  /** Contagem por situação — o que JÁ está na lista, sem consulta nova. */
  const contagem = useMemo(() => {
    const c: Record<Filtro, number> = { todos: 0, recebendo: 0, cheio: 0, pausado: 0, fora: 0 };
    for (const group of groups) {
      c.todos += 1;
      c[filtroDe(group)] += 1;
    }
    return c;
  }, [groups]);

  /** Filtro de situação + busca por nome, ID do WhatsApp ou posição — tudo no que já chegou. */
  const visiveis = useMemo(() => {
    const termos = busca.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return groups.filter((group) => {
      if (filtro !== 'todos' && filtroDe(group) !== filtro) return false;
      if (categoria === SEM_CATEGORIA && group.categoryId !== null) return false;
      if (categoria !== '' && categoria !== SEM_CATEGORIA && group.categoryId !== categoria) {
        return false;
      }
      if (termos.length === 0) return true;
      const alvo =
        `${groupDisplayName(group)} ${group.providerGroupId} #${group.position} ${group.monitorName}`.toLowerCase();
      return termos.every((t) => alvo.includes(t));
    });
  }, [busca, categoria, filtro, groups]);

  /**
   * Total de pessoas dos grupos à vista — só quando TODOS têm contagem. Somar os conhecidos
   * e calar os que nunca foram sincronizados daria um total menor que o real, com cara de
   * exato (invariante 8): nesse caso o total é travessão.
   */
  const totalPessoas = useMemo(() => {
    const ativos = visiveis.filter((g) => g.archivedAt === null);
    if (ativos.length === 0 || ativos.some((g) => g.participantsCount === null)) return null;
    return ativos.reduce((soma, g) => soma + (g.participantsCount ?? 0), 0);
  }, [visiveis]);

  const selecionaveis = visiveis.filter((g) => g.archivedAt === null && g.isActive);
  const marcados = selecionaveis.filter((g) => selecionados.has(g.id));

  /**
   * Importar os grupos ESCOLHIDOS.
   *
   * `providerGroupIds` sempre preenchido aqui: quem passa por este caminho marcou
   * grupos. Mandar a rota sem lista quer dizer outra coisa — re-sincronizar (ver
   * `resync` abaixo) —, e confundir os dois é justamente o que trazia 200 grupos de
   * família para dentro.
   */
  const runImport = useCallback(
    (monitorId: string, providerGroupIds: string[]) => {
      setRowError(null);
      importGroups.mutate(
        { workspaceId: workspace.id, monitorId, providerGroupIds },
        {
          onSuccess: (result) => {
            setImportOpen(false);
            void varrerRecemImportados(monitorId, providerGroupIds);
            toast({
              title: 'Grupos monitorados',
              description:
                importSummary(result) +
                (result.skippedByLimit > 0
                  ? ` ${result.skippedByLimit} ${
                      result.skippedByLimit === 1 ? 'grupo ficou' : 'grupos ficaram'
                    } de fora pelo limite do plano.`
                  : '') +
                (result.notFound > 0
                  ? ` ${result.notFound} ${
                      result.notFound === 1
                        ? 'grupo escolhido não está mais na lista do WhatsApp'
                        : 'grupos escolhidos não estão mais na lista do WhatsApp'
                    }.`
                  : ''),
              tone: result.skippedByLimit > 0 || result.notFound > 0 ? 'warning' : 'success',
            });
          },
          onError: (error: unknown) => {
            setImportOpen(false);
            setRowError(humanizeError(error));
          },
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `varrerRecemImportados` é declarado abaixo e só lê o que já está aqui
    [importGroups, toast, workspace.id],
  );

  /**
   * Re-sincronizar: ir ver como estão os grupos que JÁ são monitorados.
   *
   * Sem `providerGroupIds`, e é de propósito — o servidor entende isso como "atualize o
   * que já está aqui, não traga nada novo". É o gesto que o rótulo "Atualizar contagens"
   * promete, e nada além dele.
   */
  const resync = useCallback(() => {
    setRowError(null);
    const monitorId = prontos[0]?.id;
    if (monitorId === undefined) return;
    importGroups.mutate(
      { workspaceId: workspace.id, monitorId },
      {
        onSuccess: (result) =>
          toast({
            title: 'Contagens atualizadas',
            description: importSummary(result),
            tone: 'success',
          }),
        onError: (error: unknown) => setRowError(humanizeError(error)),
      },
    );
  }, [importGroups, prontos, toast, workspace.id]);

  /** Escolher sempre passa pela lista: o ponto da tela é a pessoa marcar o que quer. */
  const startImport = useCallback(() => {
    setRowError(null);
    setImportOpen(true);
  }, []);

  const togglePause = useCallback(
    (group: Group) => {
      setRowError(null);
      update.mutate(
        { workspaceId: workspace.id, groupId: group.id, isActive: !group.isActive },
        {
          onSuccess: (updated) =>
            toast({
              title: updated.isActive ? 'Grupo no rodízio' : 'Grupo pausado',
              description: updated.isActive
                ? 'Volta a receber gente nova.'
                : 'Para de receber gente nova. O histórico continua sendo registrado.',
              tone: 'success',
            }),
          onError: (error: unknown) => setRowError(humanizeError(error)),
        },
      );
    },
    [toast, update, workspace.id],
  );

  /** Pausar vários de uma vez — o mesmo `group.update` da linha, um por grupo marcado. */
  const pausarMarcados = useCallback(async () => {
    setRowError(null);
    const alvo = marcados;
    const resultados = await Promise.allSettled(
      alvo.map((group) =>
        update.mutateAsync({ workspaceId: workspace.id, groupId: group.id, isActive: false }),
      ),
    );
    const falhas = resultados.filter((r) => r.status === 'rejected');
    setSelecionados(new Set());
    if (falhas.length > 0) {
      setRowError(humanizeError((falhas[0] as PromiseRejectedResult).reason));
    }
    const ok = alvo.length - falhas.length;
    if (ok > 0) {
      toast({
        title: ok === 1 ? '1 grupo pausado' : `${ok} grupos pausados`,
        description: 'Param de receber gente nova. O histórico continua sendo registrado.',
        tone: falhas.length > 0 ? 'warning' : 'success',
      });
    }
  }, [marcados, toast, update, workspace.id]);

  /**
   * Varrer QUEM está no grupo — diferente de "Atualizar contagens", que só vai ver
   * QUANTOS são. Os dois gestos existiam como um só enquanto `group_participants` estava
   * vazia em produção: sabíamos que um grupo tinha 1218 pessoas e não sabíamos o nome de
   * nenhuma delas.
   */
  const syncPeople = useCallback(
    (group: Group) => {
      setRowError(null);
      syncParticipants.mutate(
        { workspaceId: workspace.id, monitorId: group.monitorId, groupId: group.id },
        {
          onSuccess: (result) =>
            toast({
              title: result.untrusted ? 'Nada foi alterado' : 'Pessoas sincronizadas',
              description: syncSummary(result),
              // `untrusted` não é sucesso: a tela precisa dizer que a varredura não valeu.
              tone: result.untrusted ? 'warning' : 'success',
            }),
          onError: (error: unknown) => setRowError(humanizeError(error)),
        },
      );
    },
    [syncParticipants, toast, workspace.id],
  );

  /**
   * Varrer QUEM está em vários grupos — um de cada vez (ver `runInSequence`), com o
   * progresso na tela. É o mesmo `group.syncParticipants` do botão da linha: a primeira
   * varredura continua sendo BASELINE e resposta implausível continua não escrevendo nada.
   */
  const syncMany = useCallback(
    async (alvos: readonly Group[], titulo: string) => {
      if (alvos.length === 0 || varredura !== null) return;
      setRowError(null);
      const { ok, failed } = await runInSequence(
        alvos,
        (group) =>
          syncParticipants.mutateAsync({
            workspaceId: workspace.id,
            monitorId: group.monitorId,
            groupId: group.id,
          }),
        (feitos, total) => setVarredura({ feitos, total }),
      );
      setVarredura(null);
      setSelecionados(new Set());
      const naoConfiaveis = ok.filter((r) => r.result.untrusted).length;
      const varridos = ok.length - naoConfiaveis;
      if (failed.length > 0) setRowError(humanizeError(failed[0]?.error));
      const partes = [
        varridos === 1 ? '1 grupo sincronizado.' : `${varridos} grupos sincronizados.`,
      ];
      if (naoConfiaveis > 0) {
        partes.push(
          `${naoConfiaveis} ${naoConfiaveis === 1 ? 'voltou' : 'voltaram'} sem ninguém do WhatsApp e ${naoConfiaveis === 1 ? 'ficou' : 'ficaram'} como estava${naoConfiaveis === 1 ? '' : 'm'} — tente de novo em alguns minutos.`,
        );
      }
      if (failed.length > 0) {
        partes.push(`${failed.length} ${failed.length === 1 ? 'falhou' : 'falharam'}.`);
      }
      toast({
        title: titulo,
        description: partes.join(' '),
        tone: naoConfiaveis > 0 || failed.length > 0 ? 'warning' : 'success',
      });
    },
    [syncParticipants, toast, varredura, workspace.id],
  );

  /**
   * A primeira varredura dos grupos que ACABARAM de entrar — sem a pessoa precisar clicar
   * em "Sincronizar pessoas" grupo por grupo. Sem ela, a exportação de contatos sai vazia
   * e a linha do grupo fica em "Pessoas: nunca" até alguém lembrar.
   *
   * A importação devolve contagens, não ids: relê a lista e escolhe pelo `providerGroupId`
   * que a pessoa marcou, só os que nunca foram varridos (reimportar não varre de novo).
   */
  const varrerRecemImportados = useCallback(
    async (monitorId: string, providerGroupIds: readonly string[]) => {
      const escolhidos = new Set(providerGroupIds);
      const fresco = await groupsQuery.refetch();
      const alvos = (fresco.data?.groups ?? []).filter(
        (g) =>
          g.monitorId === monitorId &&
          escolhidos.has(g.providerGroupId) &&
          g.archivedAt === null &&
          g.participantsSyncedAt === null,
      );
      await syncMany(alvos, 'Primeira varredura concluída');
    },
    [groupsQuery, syncMany],
  );

  /** "Sincronizar pessoas" da barra: os marcados, ou todos os da lista quando nada está marcado. */
  const alvosDaVarredura = marcados.length > 0 ? marcados : selecionaveis;

  const semMonitorador = monitors.length === 0;
  const semMonitoradorPronto = !semMonitorador && prontos.length === 0;
  const importDisabled = !canImport || semMonitorador || semMonitoradorPronto;

  const rowProps = {
    slug: workspace.slug,
    monitorStatus: (group: Group) => statusDoMonitor.get(group.monitorId),
    evasaoAnormal: (group: Group) => gruposComEvasaoAnormal.has(group.id),
    timezone: workspace.timezone,
    canUpdate,
    canArchive,
    canImport,
    syncing: syncParticipants.isPending || varredura !== null,
    onThreshold: setThreshold,
    onSyncPeople: syncPeople,
    onTogglePause: togglePause,
    onStop: (group: Group) => setConfirm({ kind: 'stop', group }),
    onRemove: (group: Group) => setConfirm({ kind: 'remove', group }),
    onResume: (group: Group) => {
      setRowError(null);
      unarchive.mutate(
        { workspaceId: workspace.id, groupId: group.id },
        {
          onSuccess: () =>
            toast({
              title: 'Voltou a ser monitorado',
              description: 'Voltou pausado — religue quando quiser que ele receba gente.',
              tone: 'success',
            }),
          onError: (error: unknown) => setRowError(humanizeError(error)),
        },
      );
    },
  };

  const alternarTodos = () =>
    setSelecionados(
      marcados.length === selecionaveis.length
        ? new Set()
        : new Set(selecionaveis.map((g) => g.id)),
    );
  const alternar = (group: Group) =>
    setSelecionados((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(group.id)) proximo.delete(group.id);
      else proximo.add(group.id);
      return proximo;
    });
  const selecao = canUpdate ? { marcado: (g: Group) => selecionados.has(g.id), alternar } : null;

  /**
   * Mostrar/ocultar os não monitorados. Fica disponível MESMO com a lista vazia: quando todos
   * os grupos saíram da operação, é o único caminho para voltar a monitorar ou excluir.
   */
  const alternarArquivados =
    (data?.archivedCount ?? 0) > 0 ? (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          if (includeArchived && filtro === 'fora') setFiltro('todos');
          setIncludeArchived((atual) => !atual);
        }}
      >
        {includeArchived
          ? 'Ocultar os que não monitoro'
          : `Mostrar os que não monitoro (${data?.archivedCount ?? 0})`}
      </Button>
    ) : null;

  return (
    <>
      <PageHeader
        title="Grupos"
        description="Os grupos que você monitora e quanto ainda cabe em cada um."
        action={
          canImport ? (
            <Button
              variant="primary"
              leadingIcon={<ListChecks />}
              leadingBubble
              block
              disabled={importDisabled}
              loading={importGroups.isPending}
              loadingLabel="Importando"
              onClick={startImport}
            >
              Escolher grupos
            </Button>
          ) : null
        }
      />

      {semMonitorador ? (
        <FormAlert tone="info" className="mb-3">
          Nenhum número conectado ainda. Conecte um monitorador para o MGDLead conseguir enxergar
          seus grupos.
        </FormAlert>
      ) : semMonitoradorPronto ? (
        <FormAlert tone="warning" className="mb-3">
          Nenhum dos seus números está conectado agora. Reconecte um monitorador para atualizar a
          lista de grupos.
        </FormAlert>
      ) : null}

      {limitReached && limit !== undefined ? (
        <FormAlert tone="info" className="mb-3">
          Seu plano permite {limit.limit} {limit.limit === 1 ? 'grupo' : 'grupos'} e você já está
          com {limit.current}. Grupos além disso ficam de fora da importação — deixe de monitorar um
          ou mude de plano.
        </FormAlert>
      ) : null}

      {canImport && semVarredura.length > 0 ? (
        <FormAlert tone="warning" className="mb-3">
          {semVarredura.length === 1
            ? '1 grupo ainda não foi varrido'
            : `${semVarredura.length} grupos ainda não foram varridos`}
          {' — '}
          {semVarredura.map(groupDisplayName).join(', ')}. Sabemos quantas pessoas estão lá dentro,
          não quem são: a exportação de contatos sai quase vazia e nenhuma entrada ou saída
          individual entra na conta. Toque em{' '}
          <strong className="font-medium">Sincronizar pessoas</strong> na linha do grupo.
        </FormAlert>
      ) : null}

      {rowError !== null ? <FormAlert className="mb-3">{rowError}</FormAlert> : null}

      {varredura !== null ? (
        <FormAlert tone="info" className="mb-3" aria-live="polite">
          Sincronizando pessoas: {varredura.feitos} de {varredura.total}{' '}
          {varredura.total === 1 ? 'grupo' : 'grupos'}. Um de cada vez, para não sobrecarregar o
          número — deixe esta aba aberta até terminar.
        </FormAlert>
      ) : null}

      <Card padded={false}>
        {groupsQuery.isPending ? (
          <div className="p-4 md:p-5">
            <TableSkeleton rows={3} columns={6} />
          </div>
        ) : groupsQuery.isError ? (
          <ErrorState
            title="Não conseguimos carregar os grupos"
            description={humanizeError(groupsQuery.error)}
            onRetry={() => void groupsQuery.refetch()}
          />
        ) : groups.length === 0 ? (
          <>
            {alternarArquivados ? (
              <div className="flex justify-end px-4 pt-4 md:px-5">{alternarArquivados}</div>
            ) : null}
            <EmptyState
              kind="no-data"
              icon={Users}
              title="Nenhum grupo importado ainda"
              description={
                <>
                  {O_QUE_E} Nós não criamos grupo nenhum: lemos a lista de grupos em que o número
                  conectado já está e <strong>você marca quais quer acompanhar</strong>. Os outros
                  ficam de fora — nem aparecem na sua operação.
                </>
              }
              action={
                canImport ? (
                  <Button
                    variant="primary"
                    leadingIcon={<ListChecks />}
                    disabled={importDisabled}
                    loading={importGroups.isPending}
                    loadingLabel="Importando"
                    onClick={startImport}
                    className="max-md:w-full"
                  >
                    Escolher os primeiros grupos
                  </Button>
                ) : null
              }
            />
          </>
        ) : (
          <>
            {/* ------------------------- barra de filtros ------------------------- */}
            <div className="flex flex-wrap items-center gap-3 px-4 py-4 md:px-5">
              <div className="max-w-full overflow-x-auto scrollbar-thin">
                <Tabs
                  variant="segmented"
                  value={filtro}
                  onValueChange={(valor: string) => {
                    setFiltro(valor as Filtro);
                    setSelecionados(new Set());
                  }}
                >
                  <TabsList label="Filtrar por situação">
                    <TabsTrigger value="todos" count={contagem.todos}>
                      Todos
                    </TabsTrigger>
                    <TabsTrigger value="recebendo" count={contagem.recebendo}>
                      Recebendo
                    </TabsTrigger>
                    <TabsTrigger value="cheio" count={contagem.cheio}>
                      Cheios
                    </TabsTrigger>
                    <TabsTrigger value="pausado" count={contagem.pausado}>
                      Pausados
                    </TabsTrigger>
                    {includeArchived ? (
                      <TabsTrigger value="fora" count={contagem.fora}>
                        Não monitorados
                      </TabsTrigger>
                    ) : null}
                  </TabsList>
                </Tabs>
              </div>

              <div className="hidden flex-1 md:block" />

              {canImport && !importDisabled && alvosDaVarredura.length > 0 ? (
                <Button
                  size="sm"
                  variant="neutral"
                  leadingIcon={<UserSearch />}
                  // Sem `loading`: ele esconde o texto, e o texto É o progresso.
                  disabled={varredura !== null || syncParticipants.isPending}
                  onClick={() => void syncMany(alvosDaVarredura, 'Pessoas sincronizadas')}
                >
                  {varredura !== null
                    ? `Sincronizando ${varredura.feitos} de ${varredura.total}…`
                    : marcados.length > 0
                      ? 'Sincronizar pessoas'
                      : `Sincronizar todos (${alvosDaVarredura.length})`}
                </Button>
              ) : null}

              {marcados.length > 0 ? (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground nums">
                    {marcados.length === 1 ? '1 selecionado' : `${marcados.length} selecionados`}
                  </span>
                  {/*
                    O gesto que o dono pediu por extenso: marcar TODOS de uma vez, com o nome
                    escrito — a caixinha do cabeçalho faz o mesmo, mas ninguém adivinha isso.
                  */}
                  {marcados.length < selecionaveis.length ? (
                    <Button size="sm" variant="ghost" onClick={alternarTodos}>
                      {`Selecionar todos (${selecionaveis.length})`}
                    </Button>
                  ) : null}
                  <Button size="sm" variant="ghost" onClick={() => setSelecionados(new Set())}>
                    Limpar seleção
                  </Button>
                  {canExportContacts ? (
                    <Button
                      size="sm"
                      variant="neutral"
                      leadingIcon={<Download />}
                      onClick={() => setBaixarContatos(marcados.map((g) => g.id))}
                    >
                      Baixar contatos
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="neutral"
                    leadingIcon={<Pause />}
                    loading={update.isPending}
                    onClick={() => void pausarMarcados()}
                  >
                    Pausar
                  </Button>
                </span>
              ) : null}

              <div className="w-full md:w-60">
                <Input
                  search
                  size="sm"
                  placeholder="Buscar por nome ou ID"
                  aria-label="Buscar grupo por nome ou ID"
                  value={busca}
                  onChange={(event) => setBusca(event.target.value)}
                />
              </div>

              {/* D5: recorte por categoria — só aparece quando há categoria criada. */}
              {(categoriasQuery.data?.length ?? 0) > 0 ? (
                <NativeSelect
                  size="sm"
                  aria-label="Filtrar por categoria"
                  value={categoria}
                  onChange={(event) => {
                    setCategoria(event.target.value);
                    setSelecionados(new Set());
                  }}
                  options={[
                    { value: '', label: 'Todas as categorias' },
                    ...(categoriasQuery.data ?? []).map((c) => ({
                      value: c.id,
                      label: `${c.name} (${c.groupsCount})`,
                    })),
                    { value: SEM_CATEGORIA, label: 'Sem categoria' },
                  ]}
                />
              ) : null}

              <Button
                size="sm"
                variant="ghost"
                leadingIcon={<Tags />}
                onClick={() => setCategoriasOpen(true)}
              >
                Categorias
              </Button>

              {/*
                Os contatos de TODOS os grupos (ou dos escolhidos), com filtro, num arquivo
                só. Sem seleção, o diálogo abre com todos marcados.
              */}
              {canExportContacts && marcados.length === 0 ? (
                <Button
                  size="sm"
                  variant="ghost"
                  leadingIcon={<Download />}
                  onClick={() => setBaixarContatos([])}
                >
                  Baixar contatos
                </Button>
              ) : null}

              {alternarArquivados}

              {/*
                Re-sincronizar é OUTRO gesto: atualiza a contagem dos grupos que já estão
                aqui. Não traz grupo novo — para isso existe "Escolher grupos".
              */}
              {canImport ? (
                <IconButton
                  icon={<RefreshCw className={cn(importGroups.isPending && 'animate-spin')} />}
                  label="Atualizar contagens"
                  size="sm"
                  disabled={importDisabled || importGroups.isPending}
                  onClick={resync}
                />
              ) : null}
            </div>

            {visiveis.length === 0 ? (
              <EmptyState
                kind="zero"
                compact
                icon={SearchX}
                title="Nenhum grupo aqui"
                description={
                  busca.trim() !== ''
                    ? `Nenhum dos ${groups.length} grupos bate com “${busca.trim()}”.`
                    : 'Nenhum grupo está nesta situação agora.'
                }
              />
            ) : (
              <>
                {/* ---------------------------- celular ---------------------------- */}
                <ul className="flex flex-col divide-y divide-border border-t border-border md:hidden">
                  {visiveis.map((group) => (
                    <li key={group.id}>
                      <GroupCard group={group} {...rowProps} />
                    </li>
                  ))}
                </ul>

                {/* ---------------------------- desktop ---------------------------- */}
                <div className="max-md:hidden">
                  <Table caption={`Grupos de ${workspace.name}`}>
                    <TableHeader>
                      <TableRow>
                        {selecao ? (
                          <TableHead className="w-10">
                            <Checkbox
                              aria-label="Selecionar todos os grupos da lista"
                              checked={
                                selecionaveis.length > 0 && marcados.length === selecionaveis.length
                              }
                              indeterminate={
                                marcados.length > 0 && marcados.length < selecionaveis.length
                              }
                              disabled={selecionaveis.length === 0}
                              onChange={alternarTodos}
                            />
                          </TableHead>
                        ) : null}
                        <TableHead>Grupo</TableHead>
                        <TableHead>Monitorador</TableHead>
                        <TableHead numeric>Participantes</TableHead>
                        <TableHead>Lotação</TableHead>
                        <TableHead>Situação</TableHead>
                        <TableHead>Varredura</TableHead>
                        <TableHead sticky="right">
                          <span className="sr-only">Ações</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visiveis.map((group) => (
                        <GroupRow key={group.id} group={group} selecao={selecao} {...rowProps} />
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        {selecao ? <TableCell /> : null}
                        <TableCell>Total</TableCell>
                        <TableCell />
                        <TableCell numeric>
                          {totalPessoas === null ? NO_DATA_DASH : formatNumber(totalPessoas)}
                        </TableCell>
                        <TableCell colSpan={4} />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              </>
            )}

            {/* ---------------------------- rodapé ---------------------------- */}
            <div className="flex flex-col gap-1 border-t border-border px-4 py-3.5 text-xs text-subtle-foreground md:flex-row md:items-center md:justify-between md:px-5">
              <span className="nums">
                {visiveis.length === groups.length
                  ? `${formatNumber(groups.length)} ${groups.length === 1 ? 'grupo' : 'grupos'}`
                  : `${formatNumber(visiveis.length)} de ${formatNumber(groups.length)} grupos`}
              </span>
              <span className="md:max-w-[34rem] md:text-right">
                A lotação é medida contra o seu limite de segurança, não contra a capacidade do
                WhatsApp — a folga é o que evita mandar gente para a porta fechada.
              </span>
            </div>
          </>
        )}
      </Card>

      {/* ------------------------------ diálogos ------------------------------ */}

      <BaixarContatosDialog
        open={baixarContatos !== null}
        onOpenChange={(aberto) => {
          if (!aberto) setBaixarContatos(null);
        }}
        workspaceId={workspace.id}
        initialSelection={baixarContatos ?? []}
        allowed={canExportContacts}
      />

      <CategoriesDialog
        open={categoriasOpen}
        onOpenChange={setCategoriasOpen}
        workspaceId={workspace.id}
        canEdit={canUpdate}
      />

      <ImportGroupsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        workspaceId={workspace.id}
        monitors={monitors}
        pending={importGroups.isPending}
        rowPending={archive.isPending || unarchive.isPending}
        onConfirm={runImport}
        /*
         * Deixar de monitorar é ARQUIVAR — o caminho que já existe e que guarda o
         * histórico. Não há exclusão aqui, e nunca deve haver: `participant_events`
         * aponta para o grupo, e apagar a linha levaria junto a resposta de "de onde
         * vieram as entradas de março" (invariante 5).
         */
        onStopMonitoring={(groupId, nome) => {
          setRowError(null);
          archive.mutate(
            { workspaceId: workspace.id, groupId },
            {
              onSuccess: () =>
                toast({
                  title: `“${nome}” saiu da operação`,
                  description:
                    'O grupo continua no WhatsApp e o histórico de entradas e saídas continua guardado. Dá para voltar a monitorar quando quiser.',
                  tone: 'success',
                }),
              onError: (error: unknown) => setRowError(humanizeError(error)),
            },
          );
        }}
        onResumeMonitoring={(groupId, nome) => {
          setRowError(null);
          unarchive.mutate(
            { workspaceId: workspace.id, groupId },
            {
              onSuccess: () =>
                toast({
                  title: `“${nome}” voltou`,
                  description: 'Voltou pausado — religue quando quiser que ele receba gente.',
                  tone: 'success',
                }),
              onError: (error: unknown) => setRowError(humanizeError(error)),
            },
          );
        }}
      />

      <SafetyThresholdDialog
        open={threshold !== null}
        group={threshold}
        pending={update.isPending}
        onOpenChange={(open) => {
          if (!open) setThreshold(null);
        }}
        onConfirm={(value) => {
          if (threshold === null) return;
          setRowError(null);
          update.mutate(
            { workspaceId: workspace.id, groupId: threshold.id, safetyThreshold: value },
            {
              onSuccess: (updated) => {
                setThreshold(null);
                toast({
                  title: 'Limite de segurança atualizado',
                  description:
                    updated.status === 'FULL'
                      ? 'O grupo passou a contar como cheio e saiu do rodízio.'
                      : undefined,
                  tone: updated.status === 'FULL' ? 'warning' : 'success',
                });
              },
              onError: (error: unknown) => {
                setThreshold(null);
                setRowError(humanizeError(error));
              },
            },
          );
        }}
      />

      <ConfirmDialog
        confirm={confirm}
        pending={archive.isPending || remove.isPending}
        onClose={() => setConfirm(null)}
        onConfirm={(target) => {
          setRowError(null);
          const input = { workspaceId: workspace.id, groupId: target.group.id };
          const nome = groupDisplayName(target.group);
          const onError = (error: unknown) => {
            setConfirm(null);
            setRowError(humanizeError(error));
          };

          if (target.kind === 'stop') {
            archive.mutate(input, {
              onSuccess: () => {
                /**
                 * Passa a mostrar os não monitorados na hora.
                 *
                 * Sem isto, quem deixa de monitorar vê o grupo simplesmente SUMIR da tela,
                 * e o caminho para excluir de vez fica atrás de um botão que ninguém tem
                 * motivo para procurar naquele segundo. Foi exatamente assim que nasceu o
                 * "não achei o botão de excluir o grupo" — a mesma lição já paga na tela
                 * de monitoradores.
                 */
                setIncludeArchived(true);
                setConfirm(null);
                toast({
                  title: `“${nome}” saiu da operação`,
                  description:
                    'Ele continua na lista, marcado como não monitorado. O histórico fica guardado e dá para voltar quando quiser.',
                  tone: 'success',
                });
              },
              onError,
            });
          } else {
            remove.mutate(input, {
              onSuccess: () => {
                setConfirm(null);
                toast({
                  title: `“${nome}” foi excluído`,
                  description: 'A linha saiu do banco. Nada mudou no WhatsApp.',
                  tone: 'success',
                });
              },
              onError,
            });
          }
        }}
      />
    </>
  );
}

/* ========================================================================== */
/*  Linha e cartão                                                             */
/* ========================================================================== */

interface RowProps {
  group: Group;
  /** Slug do workspace — é o que monta o link para a tela do grupo. */
  slug: string;
  timezone: string;
  canUpdate: boolean;
  canArchive: boolean;
  canImport: boolean;
  syncing: boolean;
  /** Estado do monitorador que observa o grupo (`undefined` enquanto a lista não chegou). */
  monitorStatus: (group: Group) => MonitorStatus | undefined;
  /** Rajada de saídas em aberto (GROUP_EXIT_BURST, ADR-0019) — o selo "evasão anormal". */
  evasaoAnormal: (group: Group) => boolean;
  onThreshold: (group: Group) => void;
  onSyncPeople: (group: Group) => void;
  onTogglePause: (group: Group) => void;
  onStop: (group: Group) => void;
  onRemove: (group: Group) => void;
  onResume: (group: Group) => void;
}

/**
 * O nome do grupo É o caminho para a tela dele.
 *
 * A tela de um grupo não é item de menu — detalhe nunca é. Então o caminho tem de estar
 * onde a pessoa já olha, e o nome é o único lugar em que ela já olha. Este projeto já teve
 * 15 telas sem caminho no celular; a regra que ficou é que toda tela nova nasce com um
 * caminho visível a partir de uma tela que já existe.
 *
 * `min-h-11` porque no celular o alvo de toque é 44px, mesmo quando o texto é de 13px.
 */
function GroupNameLink({ group, slug }: { group: Group; slug: string }) {
  return (
    <Link
      to={`/w/${slug}/grupos/${group.id}`}
      className={cn(
        'inline-flex min-h-11 items-center rounded-md text-left font-medium underline-offset-2 hover:underline md:min-h-0',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        group.name === null ? 'text-muted-foreground' : 'text-foreground',
      )}
      title={`Ver a linha do tempo de ${groupDisplayName(group)}`}
    >
      {groupDisplayName(group)}
    </Link>
  );
}

/**
 * O segundo caminho para a linha do tempo, com nome — redondo e discreto, ao lado do "…".
 * Fica FORA do menu de propósito: é navegação, e caminho que só aparece depois de abrir um
 * menu é caminho que depende de adivinhação.
 */
function TimelineLink({ group, slug }: { group: Group; slug: string }) {
  return (
    <Link
      to={`/w/${slug}/grupos/${group.id}`}
      className={cn(
        'hit-44 inline-grid size-7 shrink-0 place-items-center rounded-pill text-muted-foreground',
        'transition-colors duration-[var(--duration-micro)] hover:bg-muted hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
      aria-label={`Linha do tempo de ${groupDisplayName(group)}`}
      title="Linha do tempo"
    >
      <Clock aria-hidden="true" className="size-4" />
    </Link>
  );
}

/**
 * Situação + pausa + convite: são três coisas diferentes e precisam aparecer separadas.
 *
 * O selo de convite é o que menos se adivinha: **grupo sem convite nunca recebe ninguém**,
 * e nada na tela dizia isso — ele aparecia "Recebendo", com vaga, e o redirecionador o
 * pulava em silêncio. Vem com ícone além da cor (DESIGN_SYSTEM §1.3), e o conserto fica na
 * tela do grupo, a um toque no nome.
 */
function StatusCell({ group }: { group: Group }) {
  const spec = GROUP_STATUS[group.status];
  return (
    <span className="flex flex-wrap items-center gap-1">
      <Badge tone={spec.tone} dot title={spec.hint}>
        {spec.label}
      </Badge>
      {/* Pausado é decisão de pessoa; cheio é consequência da contagem. */}
      {!group.isActive && group.status !== 'ARCHIVED' ? (
        <Badge tone="neutral" icon={<Pause />} title="Fora do rodízio por escolha sua.">
          Pausado
        </Badge>
      ) : null}
      {group.archivedAt === null && !hasInvite(group) ? (
        <Badge tone="warning" icon={<LinkIcon />} title={INVITE_MISSING}>
          Sem convite
        </Badge>
      ) : null}
    </span>
  );
}

/**
 * Duas perguntas, dois carimbos — e o mesmo par de nomes que o detalhe do grupo já usa.
 *
 * A lista dizia **"Sincronizado há 18 horas"** num grupo que nunca tinha sido varrido: o
 * carimbo era o da CONTAGEM, mas quem lê entende "está tudo em dia". "Nunca" não é "faz
 * muito tempo" (invariante 8) — e, sendo a pergunta que trava a exportação e o
 * acompanhamento individual, aparece em âmbar.
 */
function SyncedCell({ group, timezone }: { group: Group; timezone: string }) {
  const pessoas = formatRelative(group.participantsSyncedAt);
  const contagem = formatRelative(group.lastSyncedAt);

  return (
    <span className="flex flex-col gap-0.5 text-xs">
      <span
        className={pessoas === null ? 'text-warning' : 'text-foreground'}
        title={
          pessoas === null
            ? PARTICIPANTS_NEVER_SYNCED
            : (formatExact(group.participantsSyncedAt, timezone) ?? undefined)
        }
      >
        Pessoas: {pessoas ?? 'nunca'}
      </span>
      <span
        className="text-2xs text-subtle-foreground"
        title={formatExact(group.lastSyncedAt, timezone) ?? undefined}
      >
        Contagem: {contagem ?? 'nunca'}
      </span>
    </span>
  );
}

/**
 * "Sei quantos" ≠ "sei quem". Um grupo com 1218 pessoas e `group_participants` vazia
 * mostrava uma barra perfeitamente cheia de confiança e não sabia o nome de ninguém.
 */
function ParticipantsKnownNote({ group }: { group: Group }) {
  if (participantsKnown(group)) return null;
  return (
    <span className="text-2xs text-subtle-foreground" title={PARTICIPANTS_NEVER_SYNCED}>
      Só a contagem — ainda não sabemos quem são
    </span>
  );
}

/**
 * Lotação no formato do painel: medidor de barrinhas + porcentagem, e embaixo o número por
 * extenso contra o LIMITE DE SEGURANÇA (nunca contra os 1024 do WhatsApp). Sem contagem, não
 * há medidor: um medidor vazio diria "grupo vazio", e "nunca sincronizei" é outra coisa.
 */
function LotacaoCell({ group }: { group: Group }) {
  const valor = occupancy(group);
  if (valor === null || group.participantsCount === null) {
    return (
      <span className="nums text-muted-foreground">
        <span aria-hidden="true">{NO_DATA_DASH}</span>
        <span className="sr-only">{occupancyLabel(group)}</span>
      </span>
    );
  }
  const vagas = seatsLeft(group) ?? 0;
  return (
    <span className="flex min-w-36 flex-col gap-1">
      {/* D3: a régua da lotação mora no CapacityMeter (85% atenção, 100% sem vaga). */}
      <CapacityMeter value={valor} label={occupancyLabel(group)} />
      <span className="nums text-2xs text-subtle-foreground">
        de {formatNumber(group.safetyThreshold)}
        {vagas === 0 ? (
          <span className="text-danger"> · sem vagas</span>
        ) : (
          <span>
            {' '}
            · {formatNumber(vagas)} {vagas === 1 ? 'vaga' : 'vagas'}
          </span>
        )}
      </span>
    </span>
  );
}

/** O monitorador que observa o grupo: estado (cor + ícone + texto) e, embaixo, o nome. */
function MonitorCell({ group, status }: { group: Group; status: MonitorStatus | undefined }) {
  return (
    <span className="flex flex-col items-start gap-1">
      {status ? <StatusPill status={status} /> : null}
      <span className="text-2xs text-subtle-foreground">{group.monitorName}</span>
      <Reserva group={group} />
    </span>
  );
}

/**
 * Quantos números conectados estão no grupo (pedido do dono, 06/10: "as várias conexões de
 * monitoradores são o backup para caso 1 número caia ou seja banido"). Com 2+, se um cair o
 * outro segue captando e assume; com 1, a queda vira buraco.
 */
function Reserva({ group }: { group: Group }) {
  if (group.archivedAt !== null || group.numerosConectados === 0) return null;
  const nomes = group.numeros.join(', ');
  return group.numerosConectados >= 2 ? (
    <Badge
      tone="success"
      icon={<ShieldCheck />}
      title={`Captando por ${group.numerosConectados} números (${nomes}). Se um cair, outro continua.`}
    >
      {group.numerosConectados} números
    </Badge>
  ) : (
    <Badge
      tone="outline"
      icon={<ShieldAlert />}
      title="Só um número está neste grupo. Se ele cair, as entradas e saídas param de ser captadas até ele voltar. Ponha outro número monitorado no grupo e importe por ele."
    >
      sem reserva
    </Badge>
  );
}

interface Selecao {
  marcado: (group: Group) => boolean;
  alternar: (group: Group) => void;
}

function GroupRow(props: RowProps & { selecao: Selecao | null }) {
  const { group, slug, timezone, selecao, monitorStatus } = props;
  const archived = group.archivedAt !== null;
  const nome = groupDisplayName(group);
  const selecionavel = !archived && group.isActive;
  const marcado = selecao !== null && selecionavel && selecao.marcado(group);
  const blocker = archived ? groupRemovalBlocker(group) : null;

  return (
    <TableRow selected={marcado}>
      {selecao ? (
        <TableCell>
          <Checkbox
            aria-label={`Selecionar ${nome}`}
            checked={marcado}
            disabled={!selecionavel}
            onChange={() => selecao.alternar(group)}
          />
        </TableCell>
      ) : null}
      <TableRowHeader>
        <span className="flex items-center gap-2.5">
          <Avatar name={nome} shape="square" size="md" />
          <TableEntity
            title={<GroupNameLink group={group} slug={slug} />}
            sub={[`#${group.position}`, adminsMembersLabel(group)].filter(Boolean).join(' · ')}
          />
          <SeloEvasaoAnormal ativo={props.evasaoAnormal(group)} />

          {group.categoryName !== null && group.categoryColor !== null ? (
            <CategoryBadge name={group.categoryName} color={group.categoryColor} />
          ) : null}
        </span>
        {/*
          Fora da operação, o motivo de não poder excluir fica ESCRITO na linha — `title`
          ou um item desabilitado dentro do menu o celular nunca mostra.
        */}
        {blocker !== null ? (
          <span className="mt-1 block max-w-[22rem] text-2xs font-normal text-subtle-foreground">
            {blocker}
          </span>
        ) : null}
      </TableRowHeader>
      <TableCell nowrap>
        <MonitorCell group={group} status={monitorStatus(group)} />
      </TableCell>
      <TableCell numeric>
        {archived || group.participantsCount === null ? (
          <span className="text-muted-foreground">{NO_DATA_DASH}</span>
        ) : (
          formatNumber(group.participantsCount)
        )}
      </TableCell>
      <TableCell>
        {archived ? (
          <span className="text-muted-foreground">{NO_DATA_DASH}</span>
        ) : (
          <span className="flex flex-col gap-1">
            <LotacaoCell group={group} />
            <ParticipantsKnownNote group={group} />
          </span>
        )}
      </TableCell>
      <TableCell nowrap>
        <StatusCell group={group} />
      </TableCell>
      <TableCell nowrap>
        <SyncedCell group={group} timezone={timezone} />
      </TableCell>
      <TableCell sticky="right">
        <span className="flex items-center justify-end gap-1">
          <RowActions {...props} />
        </span>
      </TableCell>
    </TableRow>
  );
}

/** Selo da rajada de saídas: ícone + texto, e o porquê no tooltip. */
function SeloEvasaoAnormal({ ativo }: { ativo: boolean }) {
  if (!ativo) return null;
  return (
    <Tooltip content="Muita gente saiu por conta própria deste grupo em poucas horas. Veja o aviso em Incidentes.">
      <Badge
        tone="danger"
        icon={<TrendingDown aria-hidden="true" className="size-3" />}
        tabIndex={0}
      >
        Evasão anormal
      </Badge>
    </Tooltip>
  );
}

function GroupCard(props: RowProps) {
  const { group, slug, timezone, monitorStatus } = props;
  const archived = group.archivedAt !== null;
  const nome = groupDisplayName(group);
  const blocker = archived ? groupRemovalBlocker(group) : null;
  const status = monitorStatus(group);

  return (
    <div className="flex flex-col gap-3 px-4 py-3.5">
      <div className="flex items-start gap-2.5">
        <Avatar name={nome} shape="square" size="md" className="mt-1" />
        <div className="flex min-w-0 flex-1 flex-col">
          <GroupNameLink group={group} slug={slug} />
          <span className="text-2xs text-subtle-foreground">
            {[group.monitorName, `#${group.position}`, adminsMembersLabel(group)]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {group.categoryName !== null && group.categoryColor !== null ? (
            <span className="mt-1">
              <CategoryBadge name={group.categoryName} color={group.categoryColor} />
            </span>
          ) : null}
          <span className="mt-1 flex flex-wrap gap-1">
            <Reserva group={group} />
            <SeloEvasaoAnormal ativo={props.evasaoAnormal(group)} />
          </span>
        </div>
        <span className="flex shrink-0 items-center gap-1">
          <RowActions {...props} />
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {status ? <StatusPill status={status} /> : null}
        <StatusCell group={group} />
      </div>

      {archived ? null : (
        <>
          <OccupancyBar group={group} />
          <ParticipantsKnownNote group={group} />
        </>
      )}

      {blocker !== null ? <span className="text-2xs text-subtle-foreground">{blocker}</span> : null}

      <SyncedCell group={group} timezone={timezone} />
    </div>
  );
}

/** Rótulo visível curto + o nome do grupo para leitor de tela ("Pausar Ofertas 01"). */
function rotulo(texto: string, nome: string, ligacao = ' ') {
  return (
    <>
      {texto}
      <span className="sr-only">
        {ligacao}
        {nome}
      </span>
    </>
  );
}

/**
 * As ações da linha: a linha do tempo à vista, as demais no "…".
 *
 * Eram cinco botões de texto do mesmo peso lado a lado — uma fileira assim não é um caminho,
 * é um menu para procurar dentro, e estreitava as colunas de dado até cortar o texto. Agora
 * as secundárias moram no menu; a destrutiva vai por último, separada.
 *
 * **A exceção é do estado, não do botão:** grupo nunca varrido mostra "Sincronizar pessoas"
 * FORA do menu, com contorno (`secondary` — a vaga de cor de ação é do "Escolher grupos",
 * DESIGN_SYSTEM §1.5). Varrido o grupo, a ação volta para dentro do menu.
 *
 * Esconder por `can(...)` é UX, não segurança: o servidor barra de qualquer jeito.
 */
function RowActions({
  group,
  slug,
  canUpdate,
  canArchive,
  canImport,
  syncing,
  onThreshold,
  onSyncPeople,
  onTogglePause,
  onStop,
  onRemove,
  onResume,
}: RowProps) {
  const nome = groupDisplayName(group);
  const navigate = useNavigate();
  const itens: ActionMenuItem[] = [];

  if (group.archivedAt !== null) {
    if (canArchive) {
      /**
       * Excluir aparece SEMPRE aqui — ele é o caminho, e esconder foi como nasceu o "não
       * achei o botão de excluir o grupo". Quando o banco vai recusar, o item fica
       * desabilitado e o motivo está escrito na linha.
       */
      const blocker = groupRemovalBlocker(group);
      itens.push(
        {
          id: 'resume',
          label: rotulo(GROUP_ACTION.resume, nome),
          icon: <Play />,
          onSelect: () => onResume(group),
        },
        {
          id: 'remove',
          label: rotulo(GROUP_ACTION.remove, nome),
          icon: <Trash2 />,
          destructive: true,
          separated: true,
          disabled: blocker !== null,
          onSelect: () => onRemove(group),
        },
      );
    }
  } else {
    const precisaVarrer = canImport && !participantsKnown(group);
    if (canImport && !precisaVarrer) {
      itens.push({
        id: 'sync',
        label: rotulo('Sincronizar pessoas', nome, ' de '),
        icon: <UserSearch />,
        disabled: syncing,
        onSelect: () => onSyncPeople(group),
      });
    }
    if (canUpdate) {
      itens.push(
        {
          id: 'threshold',
          label: rotulo('Limite de segurança', nome, ' de '),
          icon: <ArrowDownUp />,
          onSelect: () => onThreshold(group),
        },
        {
          id: 'pause',
          label: rotulo(group.isActive ? 'Pausar' : 'Retomar', nome),
          icon: group.isActive ? <Pause /> : <Play />,
          onSelect: () => onTogglePause(group),
        },
      );
    }
    if (canArchive) {
      itens.push({
        id: 'stop',
        label: rotulo(GROUP_ACTION.stop, nome),
        icon: <EyeOff />,
        destructive: true,
        separated: itens.length > 0,
        onSelect: () => onStop(group),
      });
    }

    if (precisaVarrer) {
      return (
        <>
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<UserSearch />}
            loading={syncing}
            loadingLabel="Sincronizando"
            aria-label={`Sincronizar pessoas de ${nome}`}
            onClick={() => onSyncPeople(group)}
            className="max-md:w-full"
          >
            Sincronizar pessoas
          </Button>
          <TimelineLink group={group} slug={slug} />
          {itens.length > 0 ? <ActionMenu label={`Ações de ${nome}`} items={itens} /> : null}
        </>
      );
    }
  }

  return (
    <>
      <TimelineLink group={group} slug={slug} />
      {itens.length > 0 ? (
        <ActionMenu
          label={`Ações de ${nome}`}
          items={[
            {
              id: 'timeline',
              label: rotulo('Linha do tempo', nome, ' de '),
              icon: <Clock />,
              onSelect: () => navigate(`/w/${slug}/grupos/${group.id}`),
            },
            ...itens,
          ]}
        />
      ) : null}
    </>
  );
}

/* ========================================================================== */
/*  Confirmação                                                                */
/* ========================================================================== */

/**
 * O que a pessoa lê ANTES de clicar. Os dois textos dizem o que sai, o que fica e o que
 * não volta — e nenhum deles fala de "arquivo", "coluna" ou "registro".
 */
const CONFIRM_COPY: Record<Confirm['kind'], { title: string; label: string; body: string }> = {
  stop: {
    title: `${GROUP_ACTION.stop} este grupo`,
    label: GROUP_ACTION.stop,
    body: 'Nada muda no WhatsApp: o grupo continua existindo e as pessoas continuam lá. O que para é a distribuição de gente nova por aqui. O histórico de entradas e saídas fica guardado, ele libera a vaga do seu plano, e dá para voltar a monitorar quando quiser.',
  },
  remove: {
    title: `${GROUP_ACTION.remove} este grupo`,
    label: 'Excluir definitivamente',
    body: 'A linha deste grupo sai do banco de vez — é a saída para o grupo importado por engano. Esta opção só aparece em grupo que você já deixou de monitorar e que nunca capturou um único evento, então nenhum dado de operação se perde. Nada muda no WhatsApp, e o grupo volta a aparecer na lista de escolha como se nunca tivesse entrado. Isso não tem desfazer.',
  },
};

interface ConfirmDialogProps {
  confirm: Confirm | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: (confirm: Confirm) => void;
}

/**
 * Confirmação simples, não a de digitar o nome: aquela é reservada a remover membro e
 * transferir propriedade. Aqui excluir já exige deixar de monitorar antes, o que é um
 * segundo passo por construção — e confirmação em fluxo repetido vira tapinha automático.
 */
function ConfirmDialog({ confirm, pending, onClose, onConfirm }: ConfirmDialogProps) {
  const copy = confirm !== null ? CONFIRM_COPY[confirm.kind] : null;

  return (
    <Dialog
      open={confirm !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={copy?.title ?? ''}
      description={confirm !== null ? `“${groupDisplayName(confirm.group)}”` : undefined}
      size="sm"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            loading={pending}
            onClick={() => {
              if (confirm !== null) onConfirm(confirm);
            }}
          >
            {copy?.label ?? ''}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted-foreground">{copy?.body ?? ''}</p>
    </Dialog>
  );
}
